"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { BrainView } from "./brainMobileViews";
const GLOBAL_VIEWS = new Set([BrainView.DEFAULT, BrainView.WAVES, BrainView.MESSAGES, BrainView.NOTIFICATIONS]);
const NON_WAVE_VIEWS = new Set([BrainView.NOTIFICATIONS, BrainView.MESSAGES, BrainView.WAVES]);
interface UseBrainMobileActiveViewParams {
  readonly firstDecisionDone: boolean;
  readonly isApp: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly pathname: string;
  readonly searchParams: ReadonlyURLSearchParams;
  readonly wave: ApiWave | null | undefined;
  readonly waveId: string | null;
}
interface UseBrainMobileActiveViewResult {
  readonly activeView: BrainView;
  readonly onViewChange: (view: BrainView) => void;
}
function getRouteDefaultView({
  createParam,
  isApp,
  pathname,
  viewParam,
  waveId
}: Pick<UseBrainMobileActiveViewParams, "isApp" | "pathname" | "waveId"> & {
  readonly createParam: string | null;
  readonly viewParam: string | null;
}): BrainView | null {
  if (createParam && isApp) {
    return BrainView.DEFAULT;
  }
  if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
    return BrainView.NOTIFICATIONS;
  }
  if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
    return BrainView.MESSAGES;
  }
  if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
    return BrainView.WAVES;
  }
  if (pathname === "/" && !waveId && !viewParam) {
    return BrainView.DEFAULT;
  }
  return null;
}
function normalizeActiveView({
  activeView,
  firstDecisionDone,
  hasWave,
  isCompleted,
  hasAuthenticatedProfile,
  isCurationWave,
  isMemesWave,
  isRankWave,
  routeDefaultView,
  wave
}: {
  readonly activeView: BrainView;
  readonly firstDecisionDone: boolean;
  readonly hasWave: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly routeDefaultView: BrainView | null;
  readonly wave: ApiWave | null | undefined;
}): BrainView {
  const hasLoadedWave = wave !== null && wave !== undefined;
  const waveDefaultView = hasLoadedWave && isRankWave && isCompleted ? BrainView.SUBMISSIONS : BrainView.DEFAULT;
  if (!hasWave) {
    if (!GLOBAL_VIEWS.has(activeView)) {
      return routeDefaultView ?? BrainView.DEFAULT;
    }
    return activeView;
  }
  if (!wave) {
    return activeView;
  }
  if (NON_WAVE_VIEWS.has(activeView)) {
    return BrainView.DEFAULT;
  }
  if (activeView === BrainView.LEADERBOARD && isRankWave && isCompleted) {
    return BrainView.SUBMISSIONS;
  }
  const shouldResetToDefault = activeView === BrainView.LEADERBOARD && !isRankWave || activeView === BrainView.SUBMISSIONS && (!isRankWave || !isCompleted) || activeView === BrainView.SALES && !isCurationWave || activeView === BrainView.WINNERS && (!isRankWave || !firstDecisionDone) || activeView === BrainView.OUTCOME && !isRankWave || activeView === BrainView.MY_VOTES && (isMemesWave && !hasAuthenticatedProfile || !isMemesWave && !isCurationWave) || activeView === BrainView.FAQ && !isMemesWave;
  return shouldResetToDefault ? waveDefaultView : activeView;
}
interface ActiveViewSelection {
  readonly contextToken: symbol;
  readonly view: BrainView;
}
export function useBrainMobileActiveView(t0) {
  const $ = _c(22);
  const {
    firstDecisionDone,
    isApp,
    isCompleted,
    hasAuthenticatedProfile,
    isCurationWave,
    isMemesWave,
    isRankWave,
    pathname,
    searchParams,
    wave,
    waveId
  } = t0;
  const [selection, setSelection] = useState(null);
  const hasWave = Boolean(waveId);
  let onViewChange;
  let t1;
  if ($[0] !== firstDecisionDone || $[1] !== hasAuthenticatedProfile || $[2] !== hasWave || $[3] !== isApp || $[4] !== isCompleted || $[5] !== isCurationWave || $[6] !== isMemesWave || $[7] !== isRankWave || $[8] !== pathname || $[9] !== searchParams || $[10] !== selection || $[11] !== wave || $[12] !== waveId) {
    const viewParam = searchParams.get("view");
    const createParam = searchParams.get("create");
    const routeDefaultView = getRouteDefaultView({
      createParam,
      isApp,
      pathname,
      viewParam,
      waveId
    });
    const shellContextKey = `shell:${pathname}:${viewParam ?? ""}`;
    const currentContextKey = waveId ? `wave:${waveId}` : shellContextKey;
    let t2;
    if ($[15] !== currentContextKey) {
      t2 = Symbol(currentContextKey);
      $[15] = currentContextKey;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    const currentContextToken = t2;
    const hasLoadedWave = wave !== null && wave !== undefined;
    const waveDefaultView = hasWave && hasLoadedWave && isRankWave && isCompleted ? BrainView.SUBMISSIONS : BrainView.DEFAULT;
    const baseView = hasWave ? waveDefaultView : routeDefaultView ?? BrainView.DEFAULT;
    const candidateView = selection?.contextToken === currentContextToken ? selection.view : baseView;
    let t3;
    if ($[17] !== currentContextToken) {
      t3 = view => {
        setSelection({
          contextToken: currentContextToken,
          view
        });
      };
      $[17] = currentContextToken;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    onViewChange = t3;
    t1 = normalizeActiveView({
      activeView: candidateView,
      firstDecisionDone,
      hasWave,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      routeDefaultView,
      wave
    });
    $[0] = firstDecisionDone;
    $[1] = hasAuthenticatedProfile;
    $[2] = hasWave;
    $[3] = isApp;
    $[4] = isCompleted;
    $[5] = isCurationWave;
    $[6] = isMemesWave;
    $[7] = isRankWave;
    $[8] = pathname;
    $[9] = searchParams;
    $[10] = selection;
    $[11] = wave;
    $[12] = waveId;
    $[13] = onViewChange;
    $[14] = t1;
  } else {
    onViewChange = $[13];
    t1 = $[14];
  }
  const activeView = t1;
  let t2;
  if ($[19] !== activeView || $[20] !== onViewChange) {
    t2 = {
      activeView,
      onViewChange
    };
    $[19] = activeView;
    $[20] = onViewChange;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
}